<template>
    <div class="sl-order-form__line-side" :class="'sl-order-form__line-side-' + side">
        <!-- Helper -->
        <div class="sl-order-form__line-band-wrapper">
            <div class="sl-order-form__line-band-content"></div>
        </div>

        <!-- Error Wrapper -->
        <div class="sl-order-form__line-content-wrapper">
            <!-- Field Wrapper -->
            <div class="sl-order-form__field-wrapper">
                <!-- Field -->
                <div
                    class="sl-order-form__gglb"
                    :class="{ 'sl-order-form__gglb--loading': loading }"
                >
                    <div
                        class="lsn-btn"
                        :class="{
                            'lsn-btn--primary-outline': !disabled,
                            'lsn-btn--gray-outline': disabled,
                            'cursor-not-allowed': disabled,
                        }"
                        :tabindex="tabIndex"
                        @click="onClick"
                        @keypress.enter="onClick"
                        @keypress.space.prevent="onClick"
                    >
                        <span class="sl-order-form__gglb-label">
                            <template v-if="loading">
                                <img src="/img/loader.gif" class="inline mx-14" />
                            </template>

                            <template v-else>
                                {{ parameter.form_label }}
                            </template>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Error -->
            <div v-if="error" class="sl-order-form__error-wrapper">
                <div class="sl-order-form__error">
                    {{ error }}
                </div>
            </div>
        </div>

        <!-- Confirmation Modal -->
        <teleport to="body">
            <lsn-modal :is-open="confirmationModalOpen" class="z-60">
                <div class="sl-prompt sl-prompt--info">
                    <div class="sl-prompt__contents">
                        <div class="sl-prompt__title">
                            {{ t('common.caution') }}
                        </div>

                        <div class="sl-prompt__text">
                            {{ t('parameters.GENERATE_GENERIC_LENS_BUTTON.confirm') }}
                        </div>
                    </div>

                    <div class="sl-prompt__actions">
                        <button
                            class="sl-prompt__button sl-prompt__button--white"
                            @click.stop="confirmationModalOpen = false"
                        >
                            {{ $t('common.cancel') }}
                        </button>

                        <button
                            class="sl-prompt__button"
                            :class="{
                                'sl-prompt__button--disabled': disabled,
                                'sl-prompt__button--primary': !disabled,
                            }"
                            @click.stop="generate()"
                        >
                            <template v-if="loading">
                                <img src="/img/loader.gif" alt="…" class="inline">
                            </template>

                            <template v-else>
                                {{ $t('parameters.GENERATE_GENERIC_LENS_BUTTON.generate') }}
                            </template>
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import {
    fieldProps,
    default as useFieldComposable,
} from '@/composables/FieldComposable';
import FormPrototype from '@/models/FormPrototype';
import GenericLensGenerator from '@/views/order/lenses/advanced/generator/GenericLensGenerator';
import NightFlexMyopiaGenerator from '@/views/order/lenses/advanced/generator/NightFlexMyopiaGenerator.js';
import MissingInputError from '@/errors/MissingInputError';


// ------------------------------------------------------------ PROPS

const props = defineProps(fieldProps);


// ------------------------------------------------------------ EMITS

const emit = defineEmits([]);


// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });
const store = useStore();
const {
    // Computed
    tabIndex,
} = useFieldComposable(props, emit, store);


// ------------------------------------------------------------ DATA

const loading = ref(false);
const confirmationModalOpen = ref(false);


// ------------------------------------------------------------ COMPUTED

const proto = computed(() =>
{
    /** @type {FormPrototype} */
    const proto = store.getters['lensOrder/getPrototype'](props.side);
    return proto;
});

const params = computed(() =>
{
    return proto.value?.getValues() ?? {};
});

/**
 * GenericLensGenerator factory.
 * @type {import('vue').ComputedRef<?GenericLensGenerator>}
 */
const generator = computed(() =>
{
    switch(proto.value?.code)
    {
        case 'NIGHTFLEXADVANCED_MYOPIA':
            return new NightFlexMyopiaGenerator();
    }

    return null;
});

const disabled = computed(() => generator.value === null || loading.value);

const warn = computed(() =>
{
    const params = proto.value.getParametersAsArray();
    for(const param of params)
    {
        if(param.getValue() != param.getDefaultValue())
        {
            return true;
        }
    }

    return false;
});


// ------------------------------------------------------------ METHODS

function onClick()
{
    if(disabled.value)
        return;

    // Validate the input
    try
    {
        generator.value.checkInput(params.value);
    }
    catch(e)
    {
        if(e instanceof MissingInputError)
        {
            notifyMissingInput(e.getParams());
            return;
        }

        throw e;
    }

    if(warn.value)
    {
        // Show the confirmation modal
        confirmationModalOpen.value = true;
    }
    else
    {
        // Generate the lens
        generate();
    }
}

/**
 * Generate the generic lens.
 */
function generate()
{
    if(loading.value)
        return;

    const form = generator.value.generate(params.value);

    // Apply the generated design to the current fitting
    form.apply(proto.value);

    // Overwrite the current fitting with a fresh template
    loading.value = true;
    store.dispatch('lensOrder/template', { side: props.side })
        .finally(async() =>
        {
            loading.value = false;
            confirmationModalOpen.value = false;

            if(store.state.lensOrder.fittings.autoplot[props.side])
            {
                await nextTick();
                store.dispatch('lensOrder/plot', props.side);
            }
        });
}

/**
 * Mark missing parameters as mandatory.
 *
 * @param {string[]} params The codes of the missing parameters.
 */
function notifyMissingInput(params)
{
    if(params.length === 0)
        return;

    const error = t('validation.required');
    props.parameter.setError(t('parameters.GENERATE_GENERIC_LENS_BUTTON.error.missing_input'));
    for(const code of params)
    {
        proto.value.getParameterByCode(code).setError(error);
    }
}
</script>

<style lang="scss" scoped>
.sl-order-form__gglb-label
{
    @apply flex justify-center items-center w-full h-full;
}
</style>
